import { useEffect, useState, useCallback } from 'react';
import { websocketService } from '../services/websocketService';
import { WebSocketMessage } from '../types/messaging';
import { Post } from '../services/postService';
import { debugLogger } from '../utils/debugLogger';
import { useAuth } from './useAuth';

export interface PostUpdateEvent {
  type: 'REACTION_ADDED' | 'REACTION_REMOVED' | 'COMMENT_ADDED' | 'SHARE_ADDED' | 'SHARE_REMOVED';
  data: any;
  timestamp: string;
}

export interface ReactionUpdateEvent {
  postId: number;
  reactionType: string;
  user: {
    userId: number;
    username: string;
    displayName: string;
    avatarUrl?: string;
  };
  newReactionCount: number;
  isAdded: boolean;
  timestamp: string;
}

export interface CommentAddedEvent {
  postId: number;
  commentId: number;
  content: string;
  author: {
    userId: number;
    username: string;
    displayName: string;
    avatarUrl?: string;
  };
  newCommentCount: number;
  timestamp: string;
}

export interface ShareUpdateEvent {
  postId: number;
  updateType: 'SHARE_ADDED' | 'SHARE_REMOVED';
  newCount: number;
  actor: {
    userId: number;
    username: string;
    displayName: string;
    avatarUrl?: string;
  };
  timestamp: string;
}

/**
 * Hook để subscribe realtime updates cho một post cụ thể
 * Tự động subscribe khi mount và unsubscribe khi unmount
 */
export const usePostWebSocket = (postId?: number) => {
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [latestUpdate, setLatestUpdate] = useState<PostUpdateEvent | null>(null);

  // Handle post updates
  const handlePostUpdate = useCallback((wsMessage: WebSocketMessage) => {
    const { type, data } = wsMessage;
    console.log('🔔 usePostWebSocket received:', { postId, type, data });
    debugLogger.log('usePostWebSocket', `Received ${type} for post ${postId}`, data);

    setLatestUpdate({ type: type as any, data, timestamp: new Date().toISOString() });

    setPost((prevPost) => {
      if (!prevPost) return prevPost;

      switch (type) {
        case 'REACTION_ADDED':
        case 'REACTION_REMOVED': {
          const event = data as ReactionUpdateEvent;
          debugLogger.log('usePostWebSocket', `🔄 Updating reaction count: ${prevPost.reactionCount} → ${event.newReactionCount}`);
          
          const updatedPost: Post = {
            ...prevPost,
            reactionCount: event.newReactionCount
          };
          
          if (prevPost.reactions) {
            updatedPost.reactions = {
              ...prevPost.reactions,
              totalCount: event.newReactionCount,
              currentUserReacted: event.user?.userId === prevPost.authorId ? true : prevPost.reactions.currentUserReacted
            };
          }
          
          return updatedPost;
        }

        case 'COMMENT_ADDED': {
          const event = data as CommentAddedEvent;
          debugLogger.log('usePostWebSocket', `💬 Updating comment count: ${prevPost.commentCount} → ${event.newCommentCount}`);
          return {
            ...prevPost,
            commentCount: event.newCommentCount
          };
        }

        case 'SHARE_ADDED':
        case 'SHARE_REMOVED': {
          const event = data as ShareUpdateEvent;
          debugLogger.log('usePostWebSocket', `🔄 Updating share count: ${prevPost.shareCount} → ${event.newCount}`);
          return {
            ...prevPost,
            shareCount: event.newCount
          };
        }

        default:
          return prevPost;
      }
    });
  }, [postId]);

  // Subscribe to specific post
  useEffect(() => {
    if (!postId || !isAuthenticated) {
      debugLogger.log('usePostWebSocket', 'Skipping subscription', { postId, isAuthenticated });
      return;
    }

    debugLogger.log('usePostWebSocket', `Subscribing to post ${postId}`);
    websocketService.subscribeToPost(postId, handlePostUpdate);

    return () => {
      debugLogger.log('usePostWebSocket', `Unsubscribing from post ${postId}`);
      websocketService.unsubscribeFromPost(postId);
    };
  }, [postId, isAuthenticated, handlePostUpdate]);

  return { 
    post, 
    setPost, 
    latestUpdate,
    clearUpdate: () => setLatestUpdate(null)
  };
};

/**
 * Hook để subscribe feed updates (bài viết mới)
 */
export const useFeedWebSocket = () => {
  const { isAuthenticated } = useAuth();
  const [newPostsAvailable, setNewPostsAvailable] = useState(false);
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [latestPost, setLatestPost] = useState<Post | null>(null);

  const handleFeedUpdate = useCallback((wsMessage: WebSocketMessage) => {
    const { type, data } = wsMessage;
    debugLogger.log('useFeedWebSocket', `Received ${type}`, data);

    if (type === 'NEW_POST') {
      setLatestPost(data as Post);
      setNewPostsCount(prev => prev + 1);
      setNewPostsAvailable(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      debugLogger.log('useFeedWebSocket', 'Not authenticated, skipping feed subscription');
      return;
    }

    debugLogger.log('useFeedWebSocket', 'Subscribing to feed updates');
    websocketService.subscribeToFeed(handleFeedUpdate);

    return () => {
      debugLogger.log('useFeedWebSocket', 'Unsubscribing from feed');
      websocketService.unsubscribeFromFeed();
    };
  }, [isAuthenticated, handleFeedUpdate]);

  const loadNewPosts = useCallback(() => {
    setNewPostsAvailable(false);
    setNewPostsCount(0);
    setLatestPost(null);
  }, []);

  return {
    newPostsAvailable,
    newPostsCount,
    latestPost,
    loadNewPosts
  };
};

/**
 * Hook để subscribe personal post updates
 * (khi ai đó like/comment bài viết của bạn)
 */
export const usePersonalPostUpdates = () => {
  const { isAuthenticated, user } = useAuth();
  const [updates, setUpdates] = useState<PostUpdateEvent[]>([]);

  const handlePersonalUpdate = useCallback((wsMessage: WebSocketMessage) => {
    const { type, data } = wsMessage;
    debugLogger.log('usePersonalPostUpdates', `Received personal update: ${type}`, data);

    setUpdates(prev => [...prev, { type: type as any, data, timestamp: new Date().toISOString() }]);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    debugLogger.log('usePersonalPostUpdates', 'Subscribing to personal post updates');
    websocketService.subscribeToPersonalPostUpdates(handlePersonalUpdate);

    return () => {
      debugLogger.log('usePersonalPostUpdates', 'Unsubscribing from personal updates');
      websocketService.unsubscribeFromPersonalPostUpdates();
    };
  }, [isAuthenticated, user, handlePersonalUpdate]);

  const clearUpdates = useCallback(() => {
    setUpdates([]);
  }, []);

  return {
    updates,
    clearUpdates,
    latestUpdate: updates[updates.length - 1] || null
  };
};
